import React, { useEffect, useState } from "react"
import { FormGroup, InputGroup } from "@blueprintjs/core"
import { Tooltip2, Popover2 } from "@blueprintjs/popover2"
import { ChatCircleText, Info, Ticket, Pencil } from "@phosphor-icons/react"
import { useMutation } from "@apollo/client"
import { UPDATE_LINE_PACKING_ALLOTMENT_LOT_CODE } from "graphql/production/production.mutation.gql"
import { Button } from "@blueprintjs/core"
import { SuccessToast } from "utils/AppToaster"
import PalletAction from "./PalletAction"
import GatePassSelector from "./Selector/GatePassSelector"
import MHCodeSelector from "./Selector/MHCodeSelector"
import {
  addPallet,
  PalletStateType,
  updatePallet,
} from "store/production.reducer"
import { useDispatch, useSelector } from "react-redux"
import { ApplicationState } from "store"
import Note from "components/Atoms/Note"
import CustomTag from "components/Atoms/CustomTag"
import { ErrorToast } from "utils/AppToaster"
import dayjs from "dayjs"

interface PalletProps {
  index: number
  production: any
  data: PalletStateType
  onRefetch?: () => void
}

export const PALLET_SIZES = [
  { value: "R-16mm", label: "R - 16mm" },
  { value: "L-17mm", label: "L - 17mm" },
  { value: "XL-18mm", label: "XL - 18mm" },
]

export const validatePallet = (pallet: PalletStateType, production: any) => {
  const errors: string[] = []

  if (!pallet.palletNumber || !pallet.palletNumber.trim()) {
    errors.push("Pallet number is required.")
  }

  if (!pallet.mhCodes?.length) {
    errors.push("At least one MH Code is required.")
  }

  const meta = production?.shipmentPlan?.metadata
    ? JSON.parse(production.shipmentPlan.metadata)
    : {}
  const capacity = production?.shipmentPlan?.skuType?.capacity ?? 1
  const requiredBoxes = pallet.isEuro
    ? meta.euro_pallet_box_count ?? 0
    : meta.regular_pallet_box_count ?? 0

  const actualBoxes =
    pallet.mhCodes?.reduce((sum, m) => sum + (m.boxes ?? 0), 0) ?? 0
  if (actualBoxes !== requiredBoxes) {
    errors.push(
      `Box quantity mismatch. Required: ${requiredBoxes}, Assigned: ${actualBoxes}.`
    )
  }

  // Check Gate Pass
  if (!pallet.gatePasses?.length) {
    errors.push("At least one Gate Pass is required.")
  } else {
    const totalWeight = pallet.gatePasses.reduce(
      (sum, g) => sum + (g.weight ?? 0),
      0
    )
    const requiredWeight = requiredBoxes * capacity
    if (+totalWeight.toFixed(2) !== +requiredWeight.toFixed(2)) {
      errors.push(
        `Weight mismatch. Required: ${requiredWeight}kg, Assigned: ${totalWeight.toFixed(
          2
        )}kg.`
      )
    }

    // Check Farmer Name
    const missingFarmer = pallet.gatePasses.some(
      g => !g.farmerName || !g.farmerName.trim()
    )
    if (missingFarmer) {
      errors.push("Farmer name missing for selected Gate Pass.")
    }
  }

  // Check pallet type
  if (pallet.isEuro === undefined || pallet.isEuro === null) {
    errors.push("Pallet type is required.")
  }

  return errors
}

// Helper component for editing Lot Code
const LotCodeEditor: React.FC<{
  lotId: string
  currentLotCode: string
  date: string
  gatePass: string
  productionCreatedDate?: string
  onRefresh?: () => void
}> = ({
  lotId,
  currentLotCode,
  date,
  gatePass,
  productionCreatedDate,
  onRefresh,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [lotCode, setLotCode] = useState(currentLotCode)
  const [prodDate, setProdDate] = useState(productionCreatedDate ?? "")
  const [updateLotCode, { loading }] = useMutation(
    UPDATE_LINE_PACKING_ALLOTMENT_LOT_CODE
  )

  // Always keep local state in sync with incoming props (e.g. after refetch)
  useEffect(() => {
    setLotCode(currentLotCode)
    setProdDate(productionCreatedDate ?? "")
  }, [currentLotCode, productionCreatedDate])

  // Reset to latest prop values whenever the popover is reopened (discard unsaved edits)
  useEffect(() => {
    if (isOpen) {
      setLotCode(currentLotCode)
      setProdDate(productionCreatedDate ?? "")
    }
  }, [isOpen])

  const handleSave = async () => {
    try {
      const changedFields: Record<string, string> = {}
      if (lotCode !== currentLotCode) changedFields.lotCode = lotCode
      if (prodDate !== (productionCreatedDate ?? ""))
        changedFields.productionCreatedDate = prodDate

      const { data } = await updateLotCode({
        variables: {
          input: {
            id: lotId,
            ...changedFields,
          },
        },
      })

      if (data?.updateLinePackingAllotmentLotCode?.ok) {
        SuccessToast("Lot code updated successfully")
        setIsOpen(false)
        setTimeout(() => onRefresh?.(), 1000)
      } else {
        ErrorToast(
          data?.updateLinePackingAllotmentLotCode?.message ||
            "Failed to update lot code"
        )
      }
    } catch (error: any) {
      ErrorToast(error.message || "Something went wrong")
    }
  }

  // Display date: prefer the locally edited prodDate, fall back to the prop
  const displayDate = prodDate || date

  return (
    <Popover2
      isOpen={isOpen}
      onInteraction={nextOpenState => setIsOpen(nextOpenState)}
      content={
        <div className="p-3 bg-white shadow-lg rounded w-64">
          <h5 className="mb-2 text-sm font-semibold">Edit Lot Code</h5>
          <InputGroup
            placeholder="Enter Lot Code"
            value={lotCode}
            onChange={(e: any) => setLotCode(e.target.value)}
          />

          <h1 className="mt-2">OR</h1>

          <div className="mt-2">
            <label className="block text-xs text-gray-500 mb-1">
              Production Created Date
            </label>
            <input
              type="date"
              className="bp4-input bp4-fill"
              value={prodDate}
              onChange={e => setProdDate(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button small onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              small
              intent="primary"
              loading={loading}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      }
      placement="bottom"
    >
      <span className="cursor-pointer px-1 rounded flex items-center gap-1">
        {gatePass} - {dayjs(displayDate).format("DD-MM-YYYY")} - {lotCode}
      </span>
    </Popover2>
  )
}

const Pallet: React.FC<PalletProps> = ({
  index,
  data,
  production,
  onRefetch,
}) => {
  const dispatch = useDispatch()
  const productionState = useSelector(
    (state: ApplicationState) => state.production.production
  )
  const pallet = productionState?.pallets?.[index]

  // 👇 Local reactive states for mismatch messages
  const [mhMismatch, setMhMismatch] = useState(false)
  const [weightMismatch, setWeightMismatch] = useState(false)

  // 👇 Automatically re-check pallet data whenever it changes
  useEffect(() => {
    if (!pallet || !production) return

    try {
      const meta = production?.shipmentPlan?.metadata
        ? JSON.parse(production.shipmentPlan.metadata)
        : {}
      const capacity = production?.shipmentPlan?.skuType?.capacity ?? 1
      const requiredBoxes = pallet.isEuro
        ? meta.euro_pallet_box_count ?? 0
        : meta.regular_pallet_box_count ?? 0
      const requiredWeight = requiredBoxes * capacity

      const hasMH = (pallet.mhCodes?.length ?? 0) > 0
      const hasGP = (pallet.gatePasses?.length ?? 0) > 0

      if (!hasMH && !hasGP) {
        setMhMismatch(false)
        setWeightMismatch(false)
        return
      }

      const actualBoxes =
        pallet.mhCodes?.reduce((sum, m) => sum + (m.boxes ?? 0), 0) ?? 0
      const actualWeight =
        pallet.gatePasses?.reduce((sum, g) => sum + (g.weight ?? 0), 0) ?? 0

      setMhMismatch(actualBoxes !== requiredBoxes)
      setWeightMismatch(+actualWeight.toFixed(2) < +requiredWeight.toFixed(2))
    } catch {
      setMhMismatch(false)
      setWeightMismatch(false)
    }
  }, [pallet, production])

  // 🧩 Handle paste of multiple pallet numbers
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("text")
    // Extract only digits from each pallet number
    const palletNos = pastedText
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(num => num.replace(/\D/g, ""))
      .filter(Boolean)
    const maxPallets = production?.shipmentPlan?.skuFilled ?? Infinity

    if (palletNos.length < maxPallets) {
      ErrorToast(
        `Incorrect pallet numbers. You pasted ${palletNos.length} pallet${
          palletNos.length !== 1 ? "s" : ""
        }, but ${maxPallets} pallet${
          maxPallets !== 1 ? "s are" : " is"
        } required.`
      )
      return
    }

    if (palletNos.length > 0 && index === 0) {
      e.preventDefault()

      const existingPalletsCount = productionState?.pallets?.length ?? 0
      const remainingSlots = maxPallets - existingPalletsCount
      if (remainingSlots <= 0) return

      const meta = production?.shipmentPlan?.metadata
        ? JSON.parse(production?.shipmentPlan?.metadata)
        : {}

      palletNos.slice(0, remainingSlots).forEach((palletNo, i) => {
        const targetIndex = existingPalletsCount + i
        const isLast = targetIndex === maxPallets - 1
        const isEuro = meta?.is_euro && isLast

        const updatedPallet: PalletStateType = {
          sequenceNumber: targetIndex + 1,
          palletNumber: palletNo,
          mhCodes: [],
          gatePasses: [],
          boxCount: 0,
          weightCount: 0,
          isEuro,
          remark: "",
          size: productionState?.pallets?.[0]?.size ?? "",
        }

        if (productionState?.pallets?.[targetIndex]) {
          dispatch(updatePallet(updatedPallet, targetIndex))
        } else {
          dispatch(addPallet(updatedPallet))
        }
      })
    }
  }

  // Size selected on the first pallet is applied to the pallets below it,
  // unless the user has already picked a different size for them.
  const handleSizeChange = (size: string) => {
    if (index !== 0) {
      dispatch(updatePallet({ ...pallet, size }, index))
      return
    }
    const previous = pallet?.size ?? ""
    productionState?.pallets?.forEach((p, i) => {
      if (i === 0 || (p.size ?? "") === previous) {
        dispatch(updatePallet({ ...p, size }, i))
      }
    })
  }

  // 🧩 Handle pallet number change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Extract only digits from the input
    const digitsOnly = value.replace(/\D/g, "")
    const updatedPallet: PalletStateType = {
      ...data,
      palletNumber: digitsOnly,
    }
    dispatch(updatePallet(updatedPallet, index))
  }

  const rejectionStats: Record<
    string,
    { totalWeight: number; totalRejected: number; percent: number }
  > = {}

  ;(productionState?.pallets ?? []).forEach(p => {
    p.gatePasses?.forEach(gp => {
      if (!gp.id) return
      if (!rejectionStats[gp.id]) {
        rejectionStats[gp.id] = { totalWeight: 0, totalRejected: 0, percent: 0 }
      }
      rejectionStats[gp.id].totalWeight += gp.weight ?? 0
      rejectionStats[gp.id].totalRejected += gp.rejectedWeight ?? 0
    })
  })

  Object.keys(rejectionStats).forEach(id => {
    const { totalWeight, totalRejected } = rejectionStats[id]
    const total = totalWeight + totalRejected // original assigned weight before rejection
    rejectionStats[id].percent = total > 0 ? (totalRejected / total) * 100 : 0
  })

  // Gate passes whose weight was fully rejected (weight 0) should not show a farmer name
  const activeFarmerNames = (pallet?.gatePasses ?? [])
    .filter(g => (g?.weight ?? 0) > 0)
    .map(g => g.farmerName)

  const getPercentColor = (value: number) => {
    if (value > 25) return "text-red-700"
    if (value > 18) return "text-yellow-600"
    if (value > 7) return "text-green-700"
    return "text-orange-600"
  }

  return (
    <section
      className="bg-blue-50 px-4 pb-2 rounded-2xl border-slate-200"
      style={{ border: "1px solid" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h6 className="text-blue-700 font-semibold text-sm whitespace-nowrap">
              Date & LOT code:
            </h6>
            <div className="flex gap-2 pt-2">
              {pallet?.lotCode?.map((l, idx) => (
                <React.Fragment key={l.id || idx}>
                  <LotCodeEditor
                    lotId={l.id!}
                    currentLotCode={l.lotCode}
                    date={l.date}
                    gatePass={l.gatePass}
                    productionCreatedDate={l.productionCreatedDate}
                    onRefresh={onRefetch}
                  />
                  {idx < (pallet.lotCode?.length ?? 0) - 1 && <span>,</span>}
                </React.Fragment>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-4">
              <h6 className="text-blue-700 font-semibold text-sm whitespace-nowrap">
                Size:
              </h6>
              <select
                value={pallet?.size ?? ""}
                onChange={e => handleSizeChange(e.target.value)}
                className={`rounded-lg border px-3 py-0.5 text-sm font-semibold cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors ${
                  pallet?.size
                    ? "border-blue-400 text-blue-700"
                    : "border-gray-300 text-gray-500"
                }`}
              >
                <option value="">Select size</option>
                {PALLET_SIZES.map(sz => (
                  <option key={sz.value} value={sz.value}>
                    {sz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="flex items-center">
          {(() => {
            const rejectedGPs = (pallet?.gatePasses ?? []).filter(
              gp => (gp?.rejectedWeight ?? 0) > 0
            )
            if (rejectedGPs?.length === 0) return null

            const text = rejectedGPs
              .map(gp => {
                const stat = rejectionStats[gp.id] ?? { percent: 0 }
                return `${gp.gatePass} - ${stat.percent.toFixed(1)}%`
              })
              .join(", ")

            const maxPercent = Math.max(
              0,
              ...rejectedGPs.map(gp => rejectionStats[gp.id]?.percent ?? 0)
            )

            return (
              <CustomTag
                minimal
                className={`rounded mr-2 p-1 px-2 font-bold ${getPercentColor(
                  maxPercent
                )}`}
              >
                Rejections: {text}
              </CustomTag>
            )
          })()}

          <Tooltip2 content={pallet?.remark} placement="left" minimal>
            <ChatCircleText
              size={20}
              weight={pallet?.remark ? "fill" : "regular"}
              className={`${
                pallet?.remark ? "text-blue-400 mr-2" : "text-blue-700 mr-2"
              }`}
            />
          </Tooltip2>
          <PalletAction
            index={index}
            remark={pallet?.remark ?? ""}
            pallet={pallet}
            production={production}
            onRefetch={onRefetch}
          />
        </div>
      </div>

      {/* Pallet Inputs */}
      <div className="grid grid-cols-5 gap-6 mt-2">
        {/* Pallet Number */}
        <FormGroup
          label={
            <>
              {pallet?.isEuro && "Euro "} Pallet no. {index + 1}{" "}
              <span className="text-red">*</span>
            </>
          }
          helperText={
            data?.palletNumber ? (
              <span className="text-[#1F6D99] text-xs">
                Pallet code: {data?.palletNumber?.slice(0, -1).slice(-10)} |{" "}
                {(() => {
                  try {
                    const meta = production?.shipmentPlan?.metadata
                      ? JSON.parse(production?.shipmentPlan?.metadata)
                      : {}
                    const isEuro = pallet?.isEuro ?? false
                    const requiredBoxes = isEuro
                      ? meta.euro_pallet_box_count ?? "--"
                      : meta.regular_pallet_box_count ?? "--"
                    return `${requiredBoxes} box`
                  } catch {
                    return "-- box"
                  }
                })()}{" "}
              </span>
            ) : null
          }
        >
          <InputGroup
            value={data?.palletNumber || ""}
            onChange={handleChange}
            onPaste={handlePaste}
            placeholder="Enter pallet number (digits only)"
          />
        </FormGroup>

        {/* MH Code */}
        <MHCodeSelector index={index} productionData={production} />

        {/* Box Qty */}
        <FormGroup
          label={
            <>
              Box Qty <span className="text-red">*</span>
            </>
          }
          helperText={
            <span className="text-[#1F6D99] text-xs flex items-center gap-2">
              <Info />
              {(pallet?.boxCount ?? 0) > 0 ? (
                <span>Gross: {(pallet?.weightCount ?? 0)?.toFixed(2)} kg</span>
              ) : (
                <span>On select Gate pass</span>
              )}
            </span>
          }
        >
          <InputGroup
            disabled
            value={pallet?.boxCount ? pallet.boxCount.toFixed(0) : "Auto"}
          />
        </FormGroup>

        {/* Gate Pass */}
        <GatePassSelector index={index} productionData={production} />

        {/* Farmer Name */}
        <FormGroup
          label={
            <>
              Farmer Name <span className="text-red">*</span>
            </>
          }
          helperText={
            <span className="text-[#1F6D99] text-xs">
              {activeFarmerNames.length > 0 ? "" : "Select Gate Pass"}
            </span>
          }
        >
          <p className="bg-slate-200 p-2 rounded">
            {activeFarmerNames.length > 0
              ? activeFarmerNames.join(", ")
              : "Select Gate Pass"}
          </p>
        </FormGroup>
      </div>

      <div className="mt-2 space-y-2">
        {/* MH note */}
        {(() => {
          const hasMH = (pallet?.mhCodes?.length ?? 0) > 0
          if (!hasMH) {
            return
          } else if (mhMismatch) {
            return (
              <Note intent="danger">
                “Yield consumed. Please select another MH code.”
              </Note>
            )
          }
          return null
        })()}

        {/* GatePass note */}
        {(() => {
          const hasGP = (pallet?.gatePasses?.length ?? 0) > 0
          if (!hasGP) {
            return
          } else if (weightMismatch) {
            return (
              <Note intent="danger">
                “Quantity consumed. Please select another Gate pass.”
              </Note>
            )
          }
          return null
        })()}

        {production?.shipmentPlan?.varietyType === "MIX" && (
          <div className="flex items-center gap-4">
            <div>
              {pallet?.mhCodes?.length > 1 ? (
                <Note intent="primary">
                  <div className="flex items-center justify-between gap-2">
                    {" "}
                    <Ticket size={20} />
                    {`Yield updated: ${pallet.mhCodes
                      .map(m => `${m.mhCode} = ${m.boxes?.toFixed(2) ?? 0} box`)
                      .join(", ")} after deducting per box weight.`}
                  </div>
                </Note>
              ) : (
                <div></div>
              )}
            </div>

            <div>
              {pallet?.gatePasses?.length > 1 && (
                <Note intent="primary">
                  <div className="flex items-center justify-between gap-2">
                    {" "}
                    <Ticket size={20} />
                    {` ${pallet.gatePasses
                      .map(
                        g => `${g.gatePass}: ${g.weight?.toFixed(2) ?? 0} kg`
                      )
                      .join(" | ")} Balance carried forward.`}
                  </div>
                </Note>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default Pallet
